import { CompRef, DocumentServicesObject } from '@wix/document-services-types';
import { isComponentEssential } from './unifiedComponents/componentSettings';
import { AppData } from '../../types/unifiedComponents';
import { PlatformContext } from '../../types/platformApi';

const appWidget = 'platform.components.AppWidget';
const appController = 'platform.components.AppController';

const getAppFromController = (
  documentServices: DocumentServicesObject,
  controllerRef: CompRef,
) => {
  const { applicationId } =
    documentServices.components.data.get(controllerRef) || {};
  const { applicationId: actualApplicationId } =
    documentServices.tpa.app.getDataByAppDefId(applicationId) || {};
  return { applicationId: actualApplicationId, appDefinitionId: applicationId };
};

const getAppFromTPA = (
  documentServices: DocumentServicesObject,
  compRef: CompRef,
) => {
  const { applicationId, appDefinitionId } =
    documentServices.components.data.get(compRef) || {};
  return { applicationId, appDefinitionId };
};

export const getComponentApp = (
  documentServices: DocumentServicesObject,
  compRef: CompRef,
): { appDefinitionId?: string; applicationId?: number } => {
  const compType = documentServices.components.getType(compRef);
  if (compType === appWidget || compType === appController) {
    return getAppFromController(documentServices, compRef);
  }
  const { controllerRef } =
    documentServices.platform.controllers.connections.getPrimaryConnection(
      compRef,
    ) || {};
  if (controllerRef) {
    return getAppFromController(documentServices, controllerRef as CompRef);
  } else {
    return getAppFromTPA(documentServices, compRef);
  }
};

type EssentialCompAppData = {
  isEssential: boolean;
  appData?: AppData;
  essentialChildren?: { compRef: CompRef; appData: AppData }[];
};

type AppDataWithComponentsEssentiality = {
  [appDefId: string]: AppData & {
    essentialsComponents: CompRef[];
    nonEssentialsComponents: CompRef[];
  };
};

const getCompAppData = (
  documentServices: DocumentServicesObject,
  compRef: CompRef,
): EssentialCompAppData | undefined => {
  const compData = documentServices.components.data.get(compRef);
  const appDefId: string = compData?.appDefinitionId || compData?.applicationId;

  if (appDefId) {
    const appData = documentServices.platform.getAppDataByAppDefId(
      appDefId,
    ) as AppData;
    return {
      isEssential: isComponentEssential(compData.widgetId, appData),
      appData,
    };
  }

  return undefined;
};

const shouldClassifyAsEssential = (
  documentServices: DocumentServicesObject,
  compRef: CompRef,
): EssentialCompAppData => {
  const compAppData = getCompAppData(documentServices, compRef);
  if (compAppData) {
    return compAppData;
  }

  const essentialChildren = documentServices.components
    .getChildren(compRef)
    .reduce((acc, childRef) => {
      const childClassification = getCompAppData(documentServices, childRef);
      if (childClassification?.isEssential) {
        acc.push({
          compRef: childRef,
          appData: childClassification.appData as AppData,
        });
      }
      return acc;
    }, [] as { compRef: CompRef; appData: AppData }[]);

  return {
    isEssential: false,
    essentialChildren,
  };
};

const addAppKeyIfNeeded = (
  dict: AppDataWithComponentsEssentiality,
  appDefId: string,
  appData?: AppData,
) => {
  if (!dict[appDefId]) {
    dict[appDefId] = {
      ...(appData as AppData),
      essentialsComponents: [],
      nonEssentialsComponents: [],
    };
  }
};

const getComponentsAppsDataWithEssentiality = (
  documentServices: DocumentServicesObject,
  components: CompRef[],
): AppDataWithComponentsEssentiality => {
  const appsData = components.reduce((acc, compRef) => {
    const { isEssential, appData, essentialChildren } =
      shouldClassifyAsEssential(documentServices, compRef);

    const appDefId = appData?.appDefinitionId ?? 'DEFAULTS';
    addAppKeyIfNeeded(acc, appDefId, appData);
    if (isEssential) {
      acc[appDefId].essentialsComponents.push(compRef);
    } else {
      if (essentialChildren && essentialChildren.length > 0) {
        essentialChildren?.forEach((child) => {
          const childAppDefId: string = child.appData.appDefinitionId;
          addAppKeyIfNeeded(acc, childAppDefId, child.appData);
          acc[childAppDefId].essentialsComponents.push(child.compRef);
        });
      }
      acc[appDefId].nonEssentialsComponents.push(compRef);
    }
    return acc;
  }, {} as AppDataWithComponentsEssentiality);

  return appsData;
};

export const handleEssentialsComponentsBeforeDelete = async (
  context: PlatformContext,
  documentServices: DocumentServicesObject,
  components: CompRef[],
): Promise<CompRef[]> => {
  let compRefsToDelete: CompRef[] = [];

  const appsData = getComponentsAppsDataWithEssentiality(
    documentServices,
    components,
  );

  for (const appData of Object.values(appsData)) {
    if (appData.essentialsComponents.length > 0) {
      const compRef = appData.essentialsComponents[0];
      await context.platformApiMethods.document.application.uninstall(
        appData,
        'TOKEN',
        { openConfirmation: true, compRef },
      );
    } else {
      compRefsToDelete = [
        ...compRefsToDelete,
        ...appData.nonEssentialsComponents,
      ];
    }
  }

  return compRefsToDelete;
};
